import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1698533189336-48d8ca9fb579?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHx2aW50YWdlJTIwcmFjaW5nJTIwY2FyJTIwZGFyayUyMGNpbmVtYXRpY3xlbnwwfHx8fDE3ODgxMTg2MjN8MA&ixlib=rb-4.1.0&q=85";

const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0 scale-110" style={{ y: bgY }}>
        <img
          src={HERO_IMG}
          alt="Vintage racing car in a dark garage"
          className="w-full h-full object-cover"
          data-testid="hero-background-image"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-[#0c0b0e] to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: textY, opacity: fade }}
        className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-32"
      >
        <RevealLine delay={0.5}>
          <span className="inline-flex items-center gap-4 text-[10px] md:text-xs font-semibold tracking-[0.4em] uppercase text-foreground/70 mb-6 md:mb-10">
            <span className="w-10 h-px bg-primary" />
            Independent E-Book Publisher — Est. on the asphalt
          </span>
        </RevealLine>

        <h1 className="font-display uppercase leading-[0.85] text-[15vw] md:text-[11.5vw] lg:text-[9.5vw]" data-testid="hero-heading">
          <RevealLine delay={0.65}>Horsepower</RevealLine>
          <RevealLine delay={0.8}>
            <span className="text-stroke">Becomes</span>
          </RevealLine>
          <RevealLine delay={0.95}>
            <span className="text-primary">History</span>
            <span className="text-foreground">.</span>
          </RevealLine>
        </h1>

        <div className="mt-8 md:mt-12 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <RevealLine delay={1.15}>
            <p className="font-editorial italic text-lg md:text-2xl text-foreground/80 max-w-md">
              Cinematic e-books chronicling the machines, the drivers, and the decades
              that made speed a religion.
            </p>
          </RevealLine>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.35, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-4"
          >
            <button
              onClick={() => {
                const el = document.querySelector("#catalog");
                if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -60, duration: 1.6 });
              }}
              className="group bg-primary text-white font-display uppercase tracking-wider text-sm px-10 py-4 hover:bg-[#a00d26] active:scale-95 transition-[background-color,transform] duration-200"
              data-testid="hero-explore-catalog-button"
            >
              Explore the Catalog
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-foreground/40"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ChevronDown size={20} />
        </motion.div>
      </motion.div>
    </section>
  );
};
